import { useState, useCallback } from 'react';
import axios from 'axios';
import { LEGISLATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import { LegislationLink, LegislationCategory, GroupedCategory } from '../types/Legislation';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getAdminHeaders = () => {
  const token = sessionStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function groupLinks(links: LegislationLink[]): GroupedCategory[] {
  const map = new Map<string, GroupedCategory>();

  for (const link of links) {
    const catKey = link.categoryId ?? '__none__';
    if (!map.has(catKey)) {
      map.set(catKey, {
        categoryId: link.categoryId,
        categoryName: link.categoryName,
        subcategories: [],
        directLinks: [],
      });
    }
    const group = map.get(catKey)!;

    if (link.subcategoryId) {
      let sub = group.subcategories.find(s => s.subcategoryId === link.subcategoryId);
      if (!sub) {
        sub = { subcategoryId: link.subcategoryId, subcategoryName: link.subcategoryName ?? '', links: [] };
        group.subcategories.push(sub);
      }
      sub.links.push(link);
    } else {
      group.directLinks.push(link);
    }
  }

  return Array.from(map.values());
}

export const useLegislation = () => {
  const [links, setLinks] = useState<LegislationLink[]>([]);
  const [grouped, setGrouped] = useState<GroupedCategory[]>([]);
  const [categories, setCategories] = useState<LegislationCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(LEGISLATION_ENDPOINTS.LINKS, { headers: getAuthHeaders() });
      const fetchedLinks: LegislationLink[] = data.links || [];
      setLinks(fetchedLinks);
      setGrouped(groupLinks(fetchedLinks));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch legislation links');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(LEGISLATION_ENDPOINTS.CATEGORIES, { headers: getAuthHeaders() });
      setCategories(data.categories || []);
    } catch {
      // non-fatal — categories are used for filter dropdowns
    }
  }, []);

  // ── Admin operations ────────────────────────────────────────────────────────

  const fetchAdminLinks = useCallback(async (): Promise<LegislationLink[]> => {
    const { data } = await axios.get(LEGISLATION_ENDPOINTS.ADMIN_LINKS, { headers: getAdminHeaders() });
    return data.links || [];
  }, []);

  const fetchAdminCategories = useCallback(async (): Promise<LegislationCategory[]> => {
    const { data } = await axios.get(LEGISLATION_ENDPOINTS.ADMIN_CATEGORIES, { headers: getAdminHeaders() });
    return data.categories || [];
  }, []);

  const createLink = useCallback(async (payload: {
    linkLabel: string; linkUrl: string; bodyText?: string;
    isActive: boolean; categoryId?: string; subcategoryId?: string;
  }): Promise<LegislationLink> => {
    const { data } = await axios.post(LEGISLATION_ENDPOINTS.ADMIN_LINKS, payload, { headers: getAdminHeaders() });
    return data;
  }, []);

  const updateLink = useCallback(async (id: number, payload: Partial<{
    linkLabel: string; linkUrl: string; bodyText: string;
    isActive: boolean; categoryId: string | null; subcategoryId: string | null;
  }>): Promise<LegislationLink> => {
    const { data } = await axios.put(LEGISLATION_ENDPOINTS.ADMIN_LINK(id), payload, { headers: getAdminHeaders() });
    return data;
  }, []);

  const deleteLink = useCallback(async (id: number): Promise<void> => {
    await axios.delete(LEGISLATION_ENDPOINTS.ADMIN_LINK(id), { headers: getAdminHeaders() });
  }, []);

  const createCategory = useCallback(async (categoryName: string): Promise<LegislationCategory> => {
    const { data } = await axios.post(LEGISLATION_ENDPOINTS.ADMIN_CATEGORIES, { categoryName }, { headers: getAdminHeaders() });
    return data;
  }, []);

  const updateCategory = useCallback(async (id: string, categoryName: string): Promise<LegislationCategory> => {
    const { data } = await axios.put(LEGISLATION_ENDPOINTS.ADMIN_CATEGORY(id), { categoryName }, { headers: getAdminHeaders() });
    return data;
  }, []);

  const deleteCategory = useCallback(async (id: string): Promise<void> => {
    await axios.delete(LEGISLATION_ENDPOINTS.ADMIN_CATEGORY(id), { headers: getAdminHeaders() });
  }, []);

  const createSubcategory = useCallback(async (subcategoryName: string, categoryId: string) => {
    const { data } = await axios.post(LEGISLATION_ENDPOINTS.ADMIN_SUBCATEGORIES, { subcategoryName, categoryId }, { headers: getAdminHeaders() });
    return data;
  }, []);

  const updateSubcategory = useCallback(async (id: string, subcategoryName: string) => {
    const { data } = await axios.put(LEGISLATION_ENDPOINTS.ADMIN_SUBCATEGORY(id), { subcategoryName }, { headers: getAdminHeaders() });
    return data;
  }, []);

  const deleteSubcategory = useCallback(async (id: string): Promise<void> => {
    await axios.delete(LEGISLATION_ENDPOINTS.ADMIN_SUBCATEGORY(id), { headers: getAdminHeaders() });
  }, []);

  return {
    links, grouped, categories, loading, error,
    fetchLinks, fetchCategories,
    fetchAdminLinks, fetchAdminCategories,
    createLink, updateLink, deleteLink,
    createCategory, updateCategory, deleteCategory,
    createSubcategory, updateSubcategory, deleteSubcategory,
  };
};
